import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { transformDateRange } from 'store/actions/globalFiltersActions'
import { sessionDataRes } from 'store/actions/sessionDataActions'
import { transformFilterDateRange } from 'store/reducers/filters'
import useUserClients from 'apollo/hooks/user/useUserClients'
import AppError from '@views/AppError/AppError'
import Loading from 'components/Loading/Loading'
import { normalizeUser } from 'models/User'
import type { ReduxState } from 'store/types'

/**
 * Guard for routes that need both user and clients to load
 * @param children protected views that should load both user and client
 * @returns loading state, error from getting user clients data & protected views
 */

const ClientGuard = ({ children }) => {
  // this acts like a flag, to ensure we dispatch all
  // necessary actions before loading the protected routes
  const [hasHydratedClientSession, setHasHydratedClientSession] =
    useState(false)

  const gfDateRange = useSelector(
    (state: ReduxState) => state.globalFilters.dateRange,
  )
  const fDateRange = useSelector((state: ReduxState) => state.filters.dateRange)

  const dispatch = useDispatch()

  const handleCompleted = useCallback(
    info => {
      if (!info) return

      const user = normalizeUser(info.user)
      const { client } = user
      const { brands } = client

      dispatch(sessionDataRes({ user, client, brands }))
      dispatch(transformDateRange(gfDateRange))
      dispatch(transformFilterDateRange(fDateRange))
      setHasHydratedClientSession(true)
    },
    [dispatch, fDateRange, gfDateRange],
  )

  const { loadUserData, called, loading, error } = useUserClients({
    handleCompleted,
  })

  useEffect(() => {
    if (!called) {
      loadUserData()
    }
  }, [called, loadUserData])

  if (error) {
    return <AppError error={error} />
  }

  if (!called || loading || !hasHydratedClientSession) {
    return <Loading />
  }

  return children
}

export default ClientGuard
